"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { mediaUrl } from "@/lib/utils";

interface Slide {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  imageUrl: string;
  mobileImageUrl?: string;
  linkUrl?: string;
  buttonText?: string;
  focalX?: number;
  focalY?: number;
  mobileFocalX?: number;
  mobileFocalY?: number;
}

interface PosterHeroProps {
  slides: Slide[];
}

const ROTATE_MS = 4800;

function displayTitle(title: string) {
  return title.replace(/\s+/g, " ").trim();
}

// Bold hero: animated brutalist poster with rotating slides, rail previews,
// controlled display type, and non-overlapping title/CTA layers.
export function PosterHero({ slides }: PosterHeroProps) {
  const reduceMotion = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const visibleSlides = useMemo(() => slides.filter((slide) => slide.imageUrl), [slides]);

  useEffect(() => {
    if (reduceMotion || paused || visibleSlides.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % visibleSlides.length);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [paused, reduceMotion, visibleSlides.length]);

  if (visibleSlides.length === 0) return null;

  const slide = visibleSlides[active] ?? visibleSlides[0];
  const title = displayTitle(slide.title);
  const watermark = displayTitle(slide.subtitle || title || "NEW SEASON");

  return (
    <section
      className="relative bg-[var(--sf-bg)] pb-3 pr-3"
      aria-roledescription="carousel"
      aria-label="Bold vitrin slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div aria-hidden className="absolute inset-x-3 bottom-0 h-3 bg-[var(--sf-accent)]" />
      <div aria-hidden className="absolute bottom-3 right-0 top-3 w-3 bg-[var(--sf-accent)]" />
      <div className="relative min-h-[34rem] overflow-hidden border-2 border-[var(--sf-text)] sm:min-h-[38rem] lg:min-h-[42rem]">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={slide.id}
            initial={reduceMotion ? false : { opacity: 0, scale: 1.06, x: 70 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 1.02, x: -70 }}
            transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
          >
            {slide.mobileImageUrl && (
              <Image
                src={mediaUrl(slide.mobileImageUrl)}
                alt={slide.title}
                fill
                loading="eager"
                sizes="(max-width: 768px) calc(100vw - 2rem), 0px"
                style={{ objectPosition: `${slide.mobileFocalX ?? 50}% ${slide.mobileFocalY ?? 50}%` }}
                className="block object-cover sm:hidden"
              />
            )}
            <Image
              src={mediaUrl(slide.imageUrl)}
              alt={slide.title}
              fill
              loading="eager"
              sizes="(max-width: 768px) 100vw, min(100vw - 2rem, 104rem)"
              style={{ objectPosition: `${slide.focalX ?? 50}% ${slide.focalY ?? 50}%` }}
              className={`${slide.mobileImageUrl ? "hidden sm:block" : "block"} object-cover`}
            />
          </motion.div>
        </AnimatePresence>

        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.58),rgba(0,0,0,0.08)_58%,rgba(0,0,0,0.42))]" />
        <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(255,255,255,0.06)_0px,rgba(255,255,255,0.06)_1px,transparent_1px,transparent_42px)] opacity-40" />

        <div className="pointer-events-none absolute inset-x-0 top-5 h-[17rem] overflow-x-clip sm:h-[19rem] lg:h-[21rem]">
          <motion.div
            key={`${slide.id}-watermark`}
            aria-hidden
            initial={reduceMotion ? false : { opacity: 0, x: 70 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.64, ease: "easeOut" }}
            className="absolute left-4 right-4 top-0 select-none overflow-x-clip whitespace-nowrap pb-2 text-left text-[clamp(3rem,13vw,13rem)] font-bold uppercase leading-[0.9] text-transparent"
            style={{ WebkitTextStroke: "2.5px var(--sf-accent)", fontFamily: "var(--sf-font-heading)" }}
          >
            {watermark}
          </motion.div>
        </div>

        <div className="relative z-10 flex min-h-[34rem] flex-col justify-end gap-6 px-5 pb-7 pt-20 sm:min-h-[38rem] sm:px-8 sm:pb-9 lg:min-h-[42rem] lg:px-14 lg:pb-12">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={`${slide.id}-copy`}
              initial={reduceMotion ? false : { opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -18 }}
              transition={{ duration: 0.34, ease: "easeOut" }}
              className="max-w-[min(58rem,calc(100vw-3rem))]"
            >
              {slide.subtitle && (
                <p
                  className="mb-3 inline-flex w-fit border-2 border-[var(--sf-text)] bg-[var(--sf-accent)] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--sf-hero-btn-text)] shadow-[3px_3px_0_var(--sf-text)]"
                  style={{ fontFamily: "var(--sf-font-heading)" }}
                >
                  {slide.subtitle}
                </p>
              )}
              <h1
                className="text-balance text-[clamp(3rem,8vw,8.5rem)] font-bold uppercase leading-[0.86] text-[var(--sf-hero-title)] drop-shadow-[0_6px_0_rgba(0,0,0,0.25)]"
                style={{ fontFamily: "var(--sf-font-heading)" }}
              >
                {title}
              </h1>
              <div className="mt-5 flex flex-col items-start gap-4">
                {slide.description && (
                  <p className="max-w-xl border-l-4 border-[var(--sf-accent)] pl-4 text-sm font-semibold leading-relaxed text-white/84 sm:text-base">
                    {slide.description}
                  </p>
                )}
                {slide.linkUrl && (
                  <Link
                    href={slide.linkUrl}
                    className="inline-flex shrink-0 items-center border-2 border-[var(--sf-text)] bg-[var(--sf-hero-btn-bg)] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.15em] text-[var(--sf-hero-btn-text)] shadow-[5px_5px_0_var(--sf-text)] transition-all hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none"
                    style={{ fontFamily: "var(--sf-font-heading)" }}
                  >
                    {slide.buttonText || "Keşfet"}
                  </Link>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {visibleSlides.length > 1 && (
          <div className="absolute bottom-5 right-5 z-20 hidden w-[min(46rem,44vw)] grid-cols-4 gap-3 lg:grid">
            {visibleSlides.slice(0, 4).map((item, index) => {
              const isActive = index === active;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActive(index)}
                  className={`group relative aspect-[5/3] overflow-hidden border-2 transition-all ${
                    isActive
                      ? "border-[var(--sf-accent)] shadow-[4px_4px_0_var(--sf-text)]"
                      : "border-[var(--sf-text)] opacity-80 hover:opacity-100"
                  }`}
                  aria-label={`${index + 1}. slide'a git`}
                  aria-current={isActive}
                >
                  <Image
                    src={mediaUrl(item.imageUrl)}
                    alt={item.title}
                    fill
                    loading={isActive ? "eager" : "lazy"}
                    sizes="12rem"
                    style={{ objectPosition: `${item.focalX ?? 50}% ${item.focalY ?? 50}%` }}
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-2 top-2 bg-[var(--sf-accent)] px-2 py-0.5 text-[10px] font-bold text-[var(--sf-hero-btn-text)]">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="absolute bottom-4 left-5 z-20 flex gap-2 sm:left-8 lg:left-14">
          {visibleSlides.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(index)}
              className="h-2 border border-[var(--sf-text)] transition-all duration-300"
              style={{
                width: active === index ? 42 : 12,
                backgroundColor: active === index ? "var(--sf-accent)" : "rgba(255,255,255,0.55)",
              }}
              aria-label={`${index + 1}. slide'a git`}
              aria-current={active === index}
            />
          ))}
        </div>

      </div>
    </section>
  );
}
